import { captureException } from '@sentry/browser';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import TagManager from 'react-gtm-module';
import { Redirect, useParams } from 'react-router-dom';

import Loading from '../../components/Loading';
import { useGetPaymentStatusLazyQuery } from '../../generated/graphql';
import { getDelay, sleep } from '../../utils/getDelay';
import getEnv from '../../utils/getEnv';
import { useContactBar } from '../../utils/hooks/useContactBarHook';
import { logUserActivity } from '../../utils/questionTracking';

/* Workflow for TnC already signed */

/**
 * Constants for exponential backoff.
 * The 1st attempt starts around 6 seconds after the initial request.
 * The 2nd attempt starts around 12 to 18 seconds after the 1st attempt.
 * The 3rd attempt starts around 24 seconds after the 2nd attempt.
 */
const BASE_DELAY = 6000; // ms
const MAX_DELAY = 24000; // ms
const MAX_RETRIES = 3;

const { REACT_APP_ADMIN_FRONTEND_URL } = getEnv();

const RETURN_TO_FOXDEN_BUTTON = 'Renewal_Complete_ReturnToFoxden';

interface CompleteParams {
  invoiceId: string;
}

const CANADA_PROVINCES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon'
];

interface MessagePageProps {
  title: string;
  message: string | ReactElement;
  policyFoxdenIds?: string[];
}

const MessagePage: React.FC<MessagePageProps> = ({
  title,
  message,
  policyFoxdenIds
}: MessagePageProps) => {
  return (
    <div className="m-auto text-lg text-center" data-testid="Completed">
      <div>
        <p>
          <b className="text-xl">{title}</b>
        </p>
        <p className="mt-4">{message}</p>
        {policyFoxdenIds && policyFoxdenIds.length > 0 ? (
          <p>
            {policyFoxdenIds.length === 1
              ? 'Your policy number is'
              : 'Your policy numbers are'}{' '}
            {policyFoxdenIds.map((id, i) => (
              <span key={id}>
                <span id={`policyFoxdenId-${i}`}>{id}</span>
                {i < policyFoxdenIds.length - 1 ? ', ' : ''}
              </span>
            ))}
            .
          </p>
        ) : null}
        <p>
          If you have any questions, contact us at{' '}
          <a href="tel:+18888784588" className="underline">
            1-888-878-4588
          </a>{' '}
          or{' '}
          <a href="mailto:support@foxquilt.com" className="underline">
            support@foxquilt.com
          </a>
          .
        </p>
      </div>
    </div>
  );
};

const InvoiceExpired: React.FC = () => (
  <div className="m-auto text-lg text-center">
    Sorry! Your quote has expired. Please contact us at{' '}
    <a href="tel:+18888784588" className="underline">
      1-888-878-4588
    </a>{' '}
    or{' '}
    <a href="mailto:sales@foxquilt.com" className="underline">
      sales@foxquilt.com
    </a>{' '}
    to complete your premium payment.
  </div>
);

interface PaymentCompleteMessageProps {
  isNotFound?: boolean;
  isExpired?: boolean;
  isUnauthorized?: boolean;
  policyFoxdenId?: string;
}

export const PaymentCompleteMessage: React.FC<PaymentCompleteMessageProps> = ({
  isExpired,
  isNotFound,
  isUnauthorized,
  policyFoxdenId
}) => {
  if (isNotFound) {
    captureException(
      new Error('Unreachable: unknown GetPaymentStatusErrorKind status')
    );
    return <Redirect to="/404" />;
  }

  const policyFoxdenIds = policyFoxdenId ? [policyFoxdenId] : undefined;

  if (isUnauthorized) {
    return (
      <MessagePage
        title={'You are not authorized to view this page'}
        message={
          'Can you please check if you are logged in with the correct account? If you think this is a mistake, please contact us.'
        }
        policyFoxdenIds={policyFoxdenIds}
      />
    );
  }

  if (policyFoxdenId) {
    return (
      <MessagePage
        title={'Thank you for signing the Terms & Conditions Document!'}
        message={
          'You will receive in your inbox shortly: your payment receipt, policy documents and customer service support numbers.'
        }
        policyFoxdenIds={policyFoxdenIds}
      />
    );
  }

  if (isExpired) {
    return <InvoiceExpired />;
  }

  throw new Error('Unreachable: unsupported payment completion status type');
};

interface TnCSignedFlowProps {
  flowKind?: string;
}

const TnCSignedFlow: React.FC<TnCSignedFlowProps> = ({ flowKind }) => {
  useContactBar();
  const attempt = useRef(0);
  const [reachMaxRetries, setReachMaxRetries] = useState(false);

  const { invoiceId } = useParams<CompleteParams>();

  const [
    loadGetPaymentStatusQuery,
    { data, error, loading, called, refetch }
  ] = useGetPaymentStatusLazyQuery({
    variables: {
      invoiceId
    }
  });

  useEffect(() => {
    data?.getPaymentStatus.forEach((status) => {
      if (status.__typename === 'PaymentStatus' && status.invoiceInfo?.client) {
        const { region } = status.invoiceInfo.client.address;
        const country = CANADA_PROVINCES.includes(region)
          ? 'Canada'
          : 'United States of America';
        TagManager.dataLayer({
          dataLayer: {
            event: 'form-progress',
            type: 'commercial',
            country,
            'state/province': region,
            step: 'Munich_Thankyou'
          }
        });
      }
    });

    const allResolved = (
      statuses: NonNullable<typeof data>['getPaymentStatus']
    ) =>
      statuses.every(
        (s) =>
          s.__typename === 'PaymentStatus' &&
          (s.policyFoxdenId || s.isRenewalReserved)
      );

    const getPaymentStatusQueryWithRetry = async (delay: number) => {
      if (loading || (data && allResolved(data.getPaymentStatus))) {
        return;
      } else if (attempt.current >= MAX_RETRIES) {
        setReachMaxRetries(true);
        return;
      }
      /**
       * TODO: Delay before the first request to address issue NCP-1898.
       * This is a temporary solution.
       * If the first response returns {policyNumber: null, isPaid: false},
       * it still incorrectly redirects to the payment page.
       */
      // Wait for the calculated delay before retrying
      await sleep(delay);
      if (!called) {
        loadGetPaymentStatusQuery(); // This is the first time the API is called
        attempt.current++;
      } else if (refetch && called) {
        const { data: refetchData } = await refetch({
          invoiceId
        });
        attempt.current++;
        if (
          refetchData && // original data provided by useGetPaymentStatusLazyQuery will not be updated immediately, so need to check refetchData.
          allResolved(refetchData.getPaymentStatus)
        ) {
          return;
        } else {
          delay = getDelay(delay, MAX_DELAY);
          await getPaymentStatusQueryWithRetry(delay);
        }
      }
    };

    getPaymentStatusQueryWithRetry(BASE_DELAY);
  }, [
    data,
    error,
    loading,
    refetch,
    called,
    invoiceId,
    loadGetPaymentStatusQuery
  ]);

  if (error) {
    return (
      <div className="m-auto text-lg text-center">
        {'Sorry, there&apos;s an error when we get payment status: '}
        {error && error.message}
      </div>
    );
  }

  if (loading || !called) {
    return <Loading />;
  }

  if (reachMaxRetries) {
    return (
      <div className="m-auto text-lg text-center">
        Sorry, there&apos;s an error after trying {MAX_RETRIES} times to get
        payment status.
      </div>
    );
  }

  if (!data) {
    return (
      <div className="m-auto text-lg text-center">
        Sorry, there&apos;s a network problem.
      </div>
    );
  }

  if (data.getPaymentStatus.some((s) => s.__typename === 'FoxdenError')) {
    return (
      <MessagePage
        title={'There is an error'}
        message={
          'You will receive in your inbox shortly: your policy documents and customer service support numbers.'
        }
      />
    );
  }

  const successStatuses = data.getPaymentStatus.filter(
    (
      s
    ): s is Extract<
      typeof data.getPaymentStatus[number],
      { __typename: 'PaymentStatus' }
    > => s.__typename === 'PaymentStatus'
  );

  if (successStatuses.length === 0) {
    captureException(new Error('Unreachable: unknown getPaymentStatus state'));
    return <Redirect to="/404" />;
  }

  //renewal reserved
  const renewalEntry = successStatuses.find((s) => s.isRenewalReserved);
  if (renewalEntry) {
    if (renewalEntry.effectiveDate && renewalEntry.transactionStartDate) {
      return (
        <RenewalReservedFlow
          effectiveDate={renewalEntry.effectiveDate}
          transactionStartDate={renewalEntry.transactionStartDate}
        />
      );
    }
    throw new Error(`Renewal reserved flow should have effective date`);
  }

  //unpaid
  if (successStatuses.some((s) => !s.isPaid)) {
    // TODO: Change ${invoiceId} to ${groupId} in this redirect URL
    // Jira ticket needed to track this change
    return <Redirect to={`/payment/${invoiceId}`} />;
  }

  //paid but no policy yet
  if (
    successStatuses.some(
      (s) => s.isPaid && !s.policyFoxdenId && !s.isRenewalReserved
    )
  ) {
    // i.e. the invoice is paid but the Policy isn't created yet (webhook should be running in parallel)
    return <Loading />;
  }

  //all paid with policyFoxdenIds
  const policyFoxdenIds = successStatuses
    .map((s) => s.policyFoxdenId)
    .filter((id): id is string => Boolean(id));

  switch (flowKind) {
    case 'RefundFullPaySuccess':
    case 'RefundInstallmentSuccess':
      return (
        <MessagePage
          title={
            'Thank you for signing the Terms & Conditions Document! Your Refund will be issued in up to 5 business days.'
          }
          message={'Please check your inbox for all updated documents.'}
          policyFoxdenIds={policyFoxdenIds}
        />
      );
    case 'WaiveSuccess':
      return (
        <MessagePage
          title={'Thank you for signing the Terms & Conditions Document!'}
          message={'Please check your inbox for all updated documents.'}
          policyFoxdenIds={policyFoxdenIds}
        />
      );
    case 'CancellationSuccess':
      return (
        <MessagePage
          title={'Thank you for signing the Lost Policy Release!'}
          message={
            'Your policy has been cancelled. Please check your inbox for all relevant documents.'
          }
          policyFoxdenIds={policyFoxdenIds}
        />
      );
    default:
      // Initial Purchase Flow
      return (
        <MessagePage
          title={'Thank you for submitting your payment!'}
          message={
            'You will receive in your inbox shortly: your payment receipt, policy documents and customer service support numbers.'
          }
          policyFoxdenIds={policyFoxdenIds}
        />
      );
  }
};

export const QuickComplete: React.FC = () => (
  <div className="m-auto text-lg text-center">
    <div>
      <p>
        <b className="text-xl">
          Thank you, your Policy details with Foxquilt have been updated.
        </b>
      </p>
      <p>
        If you have any questions, contact us at{' '}
        <a href="tel:+18888784588" className="underline">
          1-888-878-4588
        </a>{' '}
        or{' '}
        <a href="mailto:support@foxquilt.com" className="underline">
          support@foxquilt.com
        </a>
        .
      </p>
    </div>
  </div>
);

interface RenewalReservedFlowProps {
  effectiveDate: string;
  transactionStartDate: string;
}

export const RenewalReservedFlow: React.FC<RenewalReservedFlowProps> = ({
  effectiveDate,
  transactionStartDate
}) => {
  const [cookies] = useCookies(['hubspotutk']);
  const hubspotTracker = cookies.hubspotutk;

  const returnToFoxdenOnClick = () => {
    logUserActivity({
      questionId: RETURN_TO_FOXDEN_BUTTON,
      questionAnswer: 'Return to Foxden button clicked',
      hubspotTracker
    });
    window.location.href = REACT_APP_ADMIN_FRONTEND_URL;
  };

  return (
    <MessagePage
      title={'Success!'}
      message={
        <>
          <span className="block my-2">
            <span className="block">
              Policy is set to renew on {effectiveDate}.
            </span>
            <span className="block">Payment Date: {transactionStartDate}.</span>
          </span>
          <button
            className="return-foxden px-6 py-4 rounded-md text-white uppercase font-bold mt-4 mb-8 text-center"
            onClick={returnToFoxdenOnClick}
          >
            Return to Foxden
          </button>
        </>
      }
    />
  );
};

export default TnCSignedFlow;
